import React from 'react';
import {
  ActionDropDown, Badge, Dropdown,
} from '@doveit/bricks';
import { useComputedJobApplicationStatus } from '../../hooks/use-computed-job-application-status/useComputedJobApplicationStatus';
import { JobApplication } from '../../../providers/api/dtos';
import { jobApplicationStatusActionIcon, jobApplicationStatusColors, jobApplicationStatusLabels } from '../../../labels';
import { JobApplicationStatus, koJobApplicationStatus } from '../../../domain/types';
import ArchiveJobApplicationAction from '../archive-job-application-action/ArchiveJobApplicationAction';
import UpsertHRRecallAction from '../../../hr/containers/upsert-hr-recall-action/UpsertHRRecallAction';
import SendOfferAction from '../send-offer-action/SendOfferAction';
import HireApplicantAction from '../hire-applicant-action/HireApplicantAction';
import RestoreJobApplicationAction from '../restore-job-application-action/RestoreJobApplicationAction';
import UpsertHRInterviewAction from '../upsert-hr-interview-action/UpsertHRInterviewAction';

export interface UpdateJobApplicationStatusProps {
  jobApplication: JobApplication,
  showAsBadge?: boolean,
  showIcon?: boolean,
  emphasis?: 'high' | 'low',
  size?: 'XS' | 'S',
  onSuccess?: VoidFunction,
}

const UpdateJobApplicationStatus: React.FC<UpdateJobApplicationStatusProps> = ({
  jobApplication,
  showAsBadge = false,
  showIcon = false,
  emphasis = 'low',
  size = 'S',
  onSuccess,
}) => {
  const { status: computedStatus, canBeUpdated, canBeUpdatedTo } = useComputedJobApplicationStatus(jobApplication);

  return (
    (!canBeUpdated || showAsBadge)
      ? (
        <Badge
          label={jobApplicationStatusLabels[computedStatus]}
          color={jobApplicationStatusColors[computedStatus]}
          icon={showIcon ? jobApplicationStatusActionIcon[computedStatus] : undefined}
          emphasis={emphasis}
          size={size}
          aria-label="Badge per mostrare lo stato della candidatura"
        />
      )
      : (
        <ActionDropDown
          label={jobApplicationStatusLabels[computedStatus]}
          color={jobApplicationStatusColors[computedStatus]}
          iconLeft={showIcon ? { path: jobApplicationStatusActionIcon[computedStatus] } : undefined}
          emphasis={emphasis}
          size={size}
          aria-label="Selezione per aggiornare lo stato della candidatura"
        >
          {canBeUpdatedTo(JobApplicationStatus.IN_PROGRESS) && (
            <RestoreJobApplicationAction
              jobApplication={jobApplication}
              onSuccess={onSuccess}
            >
              {({ restore, loading, disabled }) => (
                <Dropdown.Option
                  label="Ripristina"
                  onClick={restore}
                  loading={loading}
                  disabled={disabled}
                  aria-label="Azione per ripristinare la candidatura"
                />
              )}
            </RestoreJobApplicationAction>
          )}
          {canBeUpdatedTo(JobApplicationStatus.ASSIGNED) && (
            <UpsertHRRecallAction
              jobApplication={jobApplication}
              canCreate
              onSuccess={onSuccess}
            >
              {({ openModal, isSaving }) => (
                <Dropdown.Option
                  label="Fissa recall"
                  onClick={openModal}
                  loading={isSaving}
                  aria-label="Azione per fissare una recall"
                />
              )}
            </UpsertHRRecallAction>
          )}
          {canBeUpdatedTo(JobApplicationStatus.INTERVIEW_SCHEDULED) && (
            <UpsertHRInterviewAction
              jobApplication={jobApplication}
              size="S"
              canCreate
              onSuccess={onSuccess}
            >
              {({ openModal, isSaving }) => (
                <Dropdown.Option
                  label="Fissa colloquio"
                  onClick={openModal}
                  loading={isSaving}
                  aria-label="Azione per fissare un colloquio"
                />
              )}
            </UpsertHRInterviewAction>
          )}
          {canBeUpdatedTo(JobApplicationStatus.OFFER) && (
            <SendOfferAction
              jobApplication={jobApplication}
              size="S"
              onSuccess={onSuccess}
            >
              {({ sendOffer, loading }) => (
                <Dropdown.Option
                  label="Invia offerta"
                  onClick={sendOffer}
                  loading={loading}
                  aria-label="Azione per inviare un'offerta"
                />
              )}
            </SendOfferAction>
          )}
          {canBeUpdatedTo(JobApplicationStatus.HIRED) && (
            <HireApplicantAction
              jobApplication={jobApplication}
              size="S"
              onSuccess={onSuccess}
            >
              {({ hireApplicant, loading }) => (
                <Dropdown.Option
                  label="Assumi"
                  onClick={hireApplicant}
                  loading={loading}
                  aria-label="Azione per assumere un candidato"
                />
              )}
            </HireApplicantAction>
          )}
          {[...koJobApplicationStatus].some((koStatus) => canBeUpdatedTo(koStatus)) && (
            <ArchiveJobApplicationAction
              jobApplication={jobApplication}
              onSuccess={onSuccess}
            >
              {({ openArchiveModal }) => (
                <Dropdown.Option
                  label="Archivia"
                  onClick={openArchiveModal}
                  aria-label="Azione per archiviare una candidatura"
                />
              )}
            </ArchiveJobApplicationAction>
          )}
        </ActionDropDown>
      )
  );
};

export default UpdateJobApplicationStatus;
